import React from 'react';
import { Typography, Row, Col, Card } from 'antd';
import {
    SyncOutlined,
    EnvironmentOutlined,
    TeamOutlined,
    SafetyOutlined,
    TableOutlined,
    ExportOutlined,
    LockOutlined,
    StarOutlined,
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const { Title, Paragraph } = Typography;

const fadeUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
    viewport: { once: true },
};

// Các tính năng chính - sẽ được translate trong component
const getFeatures = (t: any) => [
    {
        icon: <EnvironmentOutlined />,
        titleKey: 'landing__feature_interactive_maps',
        descKey: 'landing__feature_interactive_maps_desc',
        color: '#69c0ff',
    },
    {
        icon: <SyncOutlined />,
        titleKey: 'landing__feature_gis_analysis',
        descKey: 'landing__feature_gis_analysis_desc',
        color: '#95de64',
    },
    {
        icon: <TeamOutlined />,
        titleKey: 'landing__feature_collaborate',
        descKey: 'landing__feature_collaborate_desc',
        color: '#d3adf7',
    },
    {
        icon: <SafetyOutlined />,
        titleKey: 'landing__feature_security',
        descKey: 'landing__feature_security_desc',
        color: '#ffc069',
    },
];

// Danh sách tính năng nhỏ
const getFeatureList = (t: any) => [
    { icon: <EnvironmentOutlined />, titleKey: 'landing__feature_geocoding', descKey: 'landing__feature_geocoding_desc' },
    { icon: <ExportOutlined />, titleKey: 'landing__feature_data_exports', descKey: 'landing__feature_data_exports_desc' },
    { icon: <ExportOutlined />, titleKey: 'landing__feature_image_exports', descKey: 'landing__feature_image_exports_desc' },
    { icon: <LockOutlined />, titleKey: 'landing__feature_permission', descKey: 'landing__feature_permission_desc' },
    { icon: <StarOutlined />, titleKey: 'landing__feature_legends', descKey: 'landing__feature_legends_desc' },
    { icon: <TableOutlined />, titleKey: 'landing__feature_data_table', descKey: 'landing__feature_data_table_desc' },
];

const FeaturesSection: React.FC = () => {
    const { t } = useTranslation();
    const features = getFeatures(t);
    const featureList = getFeatureList(t);

    return (
        <div style={{ position: 'relative', zIndex: 1, padding: '60px 20px' }}>
            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                {/* Section Title */}
                <motion.div {...fadeUp} style={{ textAlign: 'center', marginBottom: 40 }}>
                    <Title level={2} style={{ color: '#fff', fontSize: '1.75rem', marginBottom: 12 }}>
                        {t('landing__features_title')}
                    </Title>
                    <Paragraph style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem', maxWidth: 700, margin: '0 auto' }}>
                        {t('landing__features_description')}
                    </Paragraph>
                </motion.div>

                {/* Main Features */}
                <Row gutter={[20, 20]} style={{ marginBottom: 40 }}>
                    {features.map((feature, index) => (
                        <Col xs={24} md={12} key={index}>
                            <motion.div {...fadeUp}>
                                <Card
                                    bordered={false}
                                    style={{
                                        height: '100%',
                                        borderRadius: 12,
                                        background: 'rgba(255,255,255,0.06)',
                                        backdropFilter: 'blur(10px)',
                                        color: '#fff',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                    }}
                                >
                                    <div
                                        style={{
                                            width: 44,
                                            height: 44,
                                            borderRadius: 10,
                                            background: feature.color,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 20,
                                            color: '#000',
                                            marginBottom: 12,
                                        }}
                                    >
                                        {feature.icon}
                                    </div>
                                    <Title level={4} style={{ color: '#fff', marginBottom: 8, fontSize: '1.1rem' }}>
                                        {t(feature.titleKey)}
                                    </Title>
                                    <Paragraph style={{ color: 'rgba(255,255,255,0.75)', marginBottom: 0, fontSize: '0.9rem' }}>
                                        {t(feature.descKey)}
                                    </Paragraph>
                                </Card>
                            </motion.div>
                        </Col>
                    ))}
                </Row>

                {/* Feature List */}
                <motion.div {...fadeUp}>
                    <Row gutter={[16, 16]}>
                        {featureList.map((item, index) => (
                            <Col xs={24} sm={12} md={8} key={index}>
                                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                    <div
                                        style={{
                                            color: '#69c0ff',
                                            fontSize: 16,
                                            marginTop: 2,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {item.icon}
                                    </div>
                                    <div>
                                        <div style={{ color: '#fff', fontWeight: 600, marginBottom: 2, fontSize: '0.9rem' }}>
                                            {t(item.titleKey)}
                                        </div>
                                        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem', lineHeight: 1.4 }}>
                                            {t(item.descKey)}
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </motion.div>
            </div>
        </div>
    );
};

export default FeaturesSection;
